import type { Job, JobStatus } from '@workflow/contracts'

export type JobPeriod = 'week' | 'month'

export interface JobGroup {
  key: string
  label: string
  /** The span under the label, such as "Oct 26 – Nov 1". */
  detail: string | null
  jobs: Job[]
  /** The period that holds today. */
  current: boolean
}

/** Work still to do: anything not finished, ended, or archived. */
const OPEN: readonly JobStatus[] = ['draft', 'published', 'in_progress', 'on_hold']
export const isOpenJob = (job: Job) => job.archivedAt === null && OPEN.includes(job.status)

const pad = (value: number) => String(value).padStart(2, '0')
const dayKey = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/** A month in the viewer's own time, as YYYY-MM — the value the month picker speaks. */
export const monthKey = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}`

/** "November 2026". */
export function monthName(key: string): string {
  const [year, month] = key.split('-').map(Number) as [number, number]
  return new Date(year, month - 1, 1).toLocaleDateString('en', { month: 'long', year: 'numeric' })
}

/** Weeks start on Monday. */
function weekStart(date: Date): Date {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7))
  return start
}

const short = (date: Date) => date.toLocaleDateString('en', { month: 'short', day: 'numeric' })

function periodOf(date: Date, period: JobPeriod): { key: string; start: Date; end: Date } {
  if (period === 'month') {
    const start = new Date(date.getFullYear(), date.getMonth(), 1)
    return { key: monthKey(start), start, end: new Date(date.getFullYear(), date.getMonth() + 1, 0) }
  }
  const start = weekStart(date)
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  return { key: dayKey(start), start, end }
}

function labelOf(period: JobPeriod, start: Date, end: Date, today: Date): { label: string; detail: string | null } {
  const sameYear = start.getFullYear() === today.getFullYear()
  if (period === 'month') {
    const thisMonth = start.getFullYear() === today.getFullYear() && start.getMonth() === today.getMonth()
    const name = start.toLocaleDateString('en', { month: 'long', ...(sameYear ? {} : { year: 'numeric' }) })
    return { label: thisMonth ? 'This month' : name, detail: thisMonth ? name : null }
  }
  const weeks = Math.round((weekStart(today).getTime() - start.getTime()) / (7 * 86_400_000))
  const span = `${short(start)} – ${short(end)}${sameYear ? '' : `, ${end.getFullYear()}`}`
  return { label: weeks === 0 ? 'This Week' : weeks === 1 ? 'Last Week' : span, detail: weeks <= 1 ? span : null }
}

/**
 * The work in periods by the day each job was created, newest period first;
 * inside a period the jobs keep the order they came in.
 */
export function groupJobs(jobs: readonly Job[], period: JobPeriod, today = new Date()): JobGroup[] {
  const groups = new Map<string, JobGroup>()
  const now = periodOf(today, period).key
  for (const job of jobs) {
    const { key, start, end } = periodOf(new Date(job.createdAt), period)
    let group = groups.get(key)
    if (!group) {
      group = { key, ...labelOf(period, start, end, today), jobs: [], current: key === now }
      groups.set(key, group)
    }
    group.jobs.push(job)
  }
  return [...groups.values()].sort((a, b) => b.key.localeCompare(a.key))
}
